/**
 * M8 — Rendszerimport / Onboarding oldal
 * Wizard: forrás → entitás → feltöltés → előnézet+jóváhagyás → eredmény
 * Onboarding-elágazás: self-service / ingyenes / fizetős lead
 */
import { useState, useRef, useCallback } from 'react';
import { get, post } from '../../lib/api';
import { getAccessToken } from '../../lib/auth';
import { PageHeader, Button, Badge, Card } from '../../components/ui';
import { useToast } from '../../components/ui/Toast';

// ── Típusok ────────────────────────────────────────────────────────────────────

type Source = 'szamlazzhu' | 'billingo' | 'other';
type Entity = 'partners' | 'items' | 'invoices';
type OnboardingRoute = 'self_service' | 'free_onboarding' | 'paid_onboarding';
type Step = 'source' | 'route_check' | 'free_onboarding' | 'paid_onboarding' | 'entity' | 'upload' | 'preview' | 'result';

interface PreviewRow {
  rowIndex: number;
  data: Record<string, string | number | null>;
  uncertain: boolean;
  uncertainReasons: string[];
  parseWarnings: string[];
}

interface Session {
  id: string;
  source: string;
  entity: string;
  file_name: string;
  status: string;
  total_rows: number;
  ok_rows: number;
  warning_rows: number;
  uncertain_rows: number;
  preview_json: PreviewRow[];
}

interface ImportResult { created: number; updated: number; skipped: number; errors: number; }

// ── Segédek ────────────────────────────────────────────────────────────────────

const SOURCE_LABELS: Record<Source, string> = {
  szamlazzhu: 'Számlázz.hu',
  billingo: 'Billingo',
  other: 'Egyéb rendszer',
};
const ENTITY_LABELS: Record<Entity, string> = {
  partners: 'Partnerek',
  items: 'Cikkek / termékek',
  invoices: 'Nyitott számlák',
};
const ENTITY_DESC: Record<Entity, string> = {
  partners: 'Ügyféladatbázis átvitele',
  items: 'Terméktörzs / árlista átvitele',
  invoices: 'Nyitott, kifizetetlen számlák átvitele (sorszámok megőrzésével)',
};

// ── Fő komponens ───────────────────────────────────────────────────────────────

export default function ImportOnboardingPage() {
  const { toast } = useToast();
  const [step, setStep] = useState<Step>('source');
  const [source, setSource] = useState<Source | null>(null);
  const [entity, setEntity] = useState<Entity | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: '', email: '', note: '' });
  const [leadSent, setLeadSent] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Lépés: forrás kiválasztás
  async function handleSourceSelect(src: Source) {
    setSource(src);
    if (src === 'other') {
      // Onboarding routing ellenőrzés
      setLoading(true);
      try {
        const { route } = await get<{ route: OnboardingRoute }>(`/import-onboarding/route?source=other`);
        if (route === 'free_onboarding') {
          setStep('free_onboarding');
        } else {
          setStep('paid_onboarding');
        }
      } catch {
        setStep('paid_onboarding');
      } finally {
        setLoading(false);
      }
    } else {
      setStep('entity');
    }
  }

  // Lépés: entitás kiválasztás
  function handleEntitySelect(ent: Entity) {
    setEntity(ent);
    setStep('upload');
  }

  // Lépés: feltöltés + preview
  async function handleUpload() {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast('Válassz fájlt!', 'error'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('source', source!);
      fd.append('entity', entity!);
      const token = getAccessToken();
      const res = await fetch('/api/import-onboarding/preview', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Ismeretlen hiba' }));
        throw new Error(err.error);
      }
      const data: Session = await res.json();
      setSession(data);
      setStep('preview');
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setLoading(false);
    }
  }

  // Lépés: jóváhagyás
  async function handleApprove() {
    if (!session) return;
    setLoading(true);
    try {
      const res = await post<ImportResult>(`/import-onboarding/${session.id}/approve`, {});
      setResult(res);
      setStep('result');
      toast('Import sikeresen elvégezve!', 'success');
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setLoading(false);
    }
  }

  // Lépés: elutasítás
  async function handleReject() {
    if (!session) return;
    try {
      await post(`/import-onboarding/${session.id}/reject`, {});
      toast('Import megszakítva.', 'info');
      setStep('source');
      setSource(null); setEntity(null); setSession(null);
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  }

  // Lead küldés
  async function handleLeadSubmit() {
    if (!leadForm.name || !leadForm.email) { toast('Név és e-mail kötelező.', 'error'); return; }
    setLoading(true);
    try {
      await post('/import-onboarding/onboarding-lead', {
        contactName: leadForm.name,
        contactEmail: leadForm.email,
        sourceSystem: 'other',
        note: leadForm.note,
      });
      setLeadSent(true);
      toast('Megkaptuk az igénylést, hamarosan felvesszük a kapcsolatot!', 'success');
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setLoading(false);
    }
  }

  const rows: PreviewRow[] = session?.preview_json || [];
  const uncertainCount = rows.filter(r => r.uncertain).length;
  const previewRows = rows.slice(0, 100); // UI-ban max 100 sor

  return (
    <div>
      <PageHeader
        title="Rendszerimport / Onboarding"
        subtitle="Adatok átvitele más rendszerből — kötelező előnézet és jóváhagyás"
      />

      {/* ── FORRÁS ── */}
      {step === 'source' && (
        <div>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
            Melyik rendszerből importálnál?
          </p>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {(['szamlazzhu', 'billingo', 'other'] as Source[]).map(src => (
              <Card
                key={src}
                onClick={() => !loading && handleSourceSelect(src)}
                style={{
                  cursor: loading ? 'wait' : 'pointer',
                  minWidth: 180, padding: '18px 22px',
                  border: '2px solid var(--border)',
                  transition: 'border-color .15s',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{SOURCE_LABELS[src]}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  {src === 'szamlazzhu' && 'CSV export alapján'}
                  {src === 'billingo' && 'CSV export alapján'}
                  {src === 'other' && 'Onboarding folyamat indul'}
                </div>
              </Card>
            ))}
          </div>
          {loading && <div style={{ marginTop: 16, fontSize: 13, color: 'var(--text-secondary)' }}>Ellenőrzés...</div>}
        </div>
      )}

      {/* ── INGYENES ONBOARDING ── */}
      {step === 'free_onboarding' && (
        <Card style={{ maxWidth: 520 }}>
          <div style={{ fontSize: 22, marginBottom: 8 }}>🎉</div>
          <h3 style={{ margin: '0 0 10px', fontSize: 16 }}>Ingyenes onboarding — éves Pro csomag</h3>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 20 }}>
            Az éves Pro előfizetésed tartalmaz ingyenes adatátviteli segítséget. Csapatunk elvégzi az importot helyetted — vedd fel velünk a kapcsolatot.
          </p>
          <Button onClick={() => { window.location.href = 'mailto:support@modulum.hu?subject=Ingyenes+onboarding'; }}>
            support@modulum.hu írása
          </Button>
          <Button variant="secondary" style={{ marginLeft: 10 }} onClick={() => setStep('source')}>Vissza</Button>
        </Card>
      )}

      {/* ── FIZETŐS ONBOARDING LEAD ── */}
      {step === 'paid_onboarding' && (
        <Card style={{ maxWidth: 520 }}>
          <h3 style={{ margin: '0 0 10px', fontSize: 16 }}>Adatátvitel egyéb rendszerből</h3>
          {leadSent ? (
            <div style={{ color: 'var(--success)', fontWeight: 600, fontSize: 14 }}>
              Köszönjük! Hamarosan felvesszük a kapcsolatot.
            </div>
          ) : (
            <>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 20 }}>
                Az importhoz segítséget kínálunk — add meg elérhetőségedet és csapatunk ajánlatot küld.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <input
                  placeholder="Neved"
                  value={leadForm.name}
                  onChange={e => setLeadForm(f => ({ ...f, name: e.target.value }))}
                  style={inputStyle}
                />
                <input
                  type="email"
                  placeholder="E-mail cím"
                  value={leadForm.email}
                  onChange={e => setLeadForm(f => ({ ...f, email: e.target.value }))}
                  style={inputStyle}
                />
                <textarea
                  placeholder="Honnan importálnál, melyik rendszer? (opcionális)"
                  value={leadForm.note}
                  onChange={e => setLeadForm(f => ({ ...f, note: e.target.value }))}
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical' }}
                />
                <div style={{ display: 'flex', gap: 10 }}>
                  <Button onClick={handleLeadSubmit} disabled={loading} loading={loading}>Ajánlatkérés küldése</Button>
                  <Button variant="secondary" onClick={() => setStep('source')}>Vissza</Button>
                </div>
              </div>
            </>
          )}
        </Card>
      )}

      {/* ── ENTITÁS ── */}
      {step === 'entity' && (
        <div>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 24 }}>
            Mit importálsz <strong>{SOURCE_LABELS[source!]}</strong>-ból?
          </p>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {(['partners', 'items', 'invoices'] as Entity[]).map(ent => (
              <Card
                key={ent}
                onClick={() => handleEntitySelect(ent)}
                style={{ cursor: 'pointer', minWidth: 200, padding: '18px 22px', border: '2px solid var(--border)' }}
              >
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{ENTITY_LABELS[ent]}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{ENTITY_DESC[ent]}</div>
              </Card>
            ))}
          </div>
          <Button variant="ghost" onClick={() => setStep('source')} style={{ marginTop: 16 }}>← Vissza</Button>
        </div>
      )}

      {/* ── FELTÖLTÉS ── */}
      {step === 'upload' && (
        <Card style={{ maxWidth: 520 }}>
          <h3 style={{ margin: '0 0 8px', fontSize: 15 }}>
            {ENTITY_LABELS[entity!]} importálása — {SOURCE_LABELS[source!]}
          </h3>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
            Exportáld a fájlt {SOURCE_LABELS[source!]}-ból CSV formátumban, majd töltsd fel itt.
            Az import <strong>nem fut azonnal</strong> — előbb megtekintheited és jóváhagyod.
          </p>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
            <input ref={fileRef} type="file" accept=".csv,.txt" style={{ flex: 1, fontSize: 13 }} />
            <Button onClick={handleUpload} disabled={loading} loading={loading}>
              Előnézet generálása
            </Button>
          </div>
          <Button variant="ghost" onClick={() => setStep('entity')}>← Vissza</Button>
        </Card>
      )}

      {/* ── ELŐNÉZET ── */}
      {step === 'preview' && session && (
        <div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0, fontSize: 15 }}>Előnézet: {session.file_name}</h3>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
                {session.total_rows} sor összesen —{' '}
                <span style={{ color: 'var(--success)' }}>{session.ok_rows} rendben</span>,{' '}
                {session.warning_rows > 0 && <><span style={{ color: 'var(--warning)' }}>{session.warning_rows} figyelmeztetés</span>, </>}
                {session.uncertain_rows > 0 && <><span style={{ color: 'var(--error)' }}>{session.uncertain_rows} kézi ellenőrzés szükséges</span></>}
              </div>
            </div>
            <Button onClick={handleApprove} disabled={loading} loading={loading}>
              Jóváhagyás és importálás
            </Button>
            <Button variant="secondary" onClick={handleReject} disabled={loading}>
              Megszakítás
            </Button>
          </div>

          {uncertainCount > 0 && (
            <div style={{ background: 'var(--warning-bg, #fffbeb)', border: '1px solid var(--warning, #f59e0b)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
              <strong>⚠ {uncertainCount} sor kézi ellenőrzést igényel</strong> — narancssárga sorként jelölve.
              Az importálás ezeket is beemeli, de „kézi ellenőrzés" megjegyzéssel.
            </div>
          )}

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={thStyle}>#</th>
                  <th style={thStyle}>Állapot</th>
                  {Object.keys(previewRows[0]?.data || {}).slice(0, 6).map(k => (
                    <th key={k} style={thStyle}>{k}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map(row => (
                  <tr
                    key={row.rowIndex}
                    style={{
                      borderBottom: '1px solid var(--border)',
                      background: row.uncertain ? 'var(--warning-bg, #fffbeb)' : undefined,
                    }}
                  >
                    <td style={tdStyle}>{row.rowIndex}</td>
                    <td style={tdStyle}>
                      {row.uncertain
                        ? <Badge color="warning" title={row.uncertainReasons.join(', ')}>Ellenőrzendő</Badge>
                        : row.parseWarnings.length > 0
                          ? <Badge color="info">Figyelmeztetés</Badge>
                          : <Badge color="success">OK</Badge>
                      }
                    </td>
                    {Object.keys(previewRows[0]?.data || {}).slice(0, 6).map(k => (
                      <td key={k} style={{ ...tdStyle, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {String(row.data[k] ?? '—')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > 100 && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>
              (Az előnézet az első 100 sort mutatja. Összesen {rows.length} sor kerül importálásra.)
            </div>
          )}
        </div>
      )}

      {/* ── EREDMÉNY ── */}
      {step === 'result' && result && (
        <Card style={{ maxWidth: 440 }}>
          <div style={{ fontSize: 24, marginBottom: 12 }}>✅</div>
          <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>Import kész</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'Létrehozva', value: result.created, color: 'var(--success)' },
              { label: 'Frissítve', value: result.updated, color: 'var(--info, #3b82f6)' },
              { label: 'Kihagyva', value: result.skipped, color: 'var(--text-secondary)' },
              { label: 'Hiba', value: result.errors, color: result.errors > 0 ? 'var(--error)' : 'var(--text-secondary)' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ textAlign: 'center', padding: '12px', background: 'var(--surface-secondary, #f9fafb)', borderRadius: 8 }}>
                <div style={{ fontSize: 24, fontWeight: 800, color }}>{value}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{label}</div>
              </div>
            ))}
          </div>
          <Button onClick={() => { setStep('source'); setSource(null); setEntity(null); setSession(null); setResult(null); }}>
            Új import
          </Button>
        </Card>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '9px 12px', borderRadius: 7, border: '1px solid var(--border)',
  fontSize: 13, width: '100%', boxSizing: 'border-box',
};
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '7px 10px', fontWeight: 600,
  color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase',
};
const tdStyle: React.CSSProperties = { padding: '7px 10px' };
